/**
 * ExportMenu — export the current dashboard KPI snapshot as PDF / Excel / CSV.
 * Reads the shared DashboardContext for scope (site/time/department) so the
 * export matches what's on screen. Server enforces per-metric RBAC + scoping.
 */
import React, { useState } from 'react';
import {
  Download, FileText, FileSpreadsheet, FileDown, Presentation, Loader2,
} from 'lucide-react';
import { useDashboard } from '../context/DashboardContext';
import { exportDashboard } from '../api/client';

const FORMATS = [
  { id: 'pdf', label: 'PDF', Icon: FileText },
  // A deck the EHS team presents from and re-orders, with every chart on screen
  // rendered as a slide image. The PDF stays the finished record.
  { id: 'pptx', label: 'PowerPoint', Icon: Presentation },
  { id: 'excel', label: 'Excel', Icon: FileSpreadsheet },
  { id: 'csv', label: 'CSV', Icon: FileDown },
];

// Every spec key the export endpoint understands, listed once. Adding a block
// type to a preset then needs no change here — the previous hard-coded copy-outs
// were how the safety analytics panels came to be on screen but absent from the
// export.
const SPEC_KEYS = [
  'sections', 'breakdowns', 'comparison_metrics', 'comparisons', 'trend', 'yoy',
  'pyramid', 'causes', 'high_risk_locations', 'repeat_incidents', 'risk_matrix',
  'metric_bars', 'series', 'rate_trend',
  'compliance_register', 'training_breakdowns',
];

const present = (v) => (Array.isArray(v) ? v.length > 0 : Boolean(v));

export default function ExportMenu({ module, metrics: metricsProp, spec: staticSpec, specBuilder }) {
  const { siteId, time, departmentId, dimensions, fyStartMonth, standard, visibleWidgetIds } = useDashboard();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);

  // Build the export spec from the widgets currently on the dashboard when a
  // specBuilder is supplied, so the report matches what's on screen.
  const spec = specBuilder ? specBuilder(visibleWidgetIds) : staticSpec;
  const metrics = spec?.metrics || metricsProp || [];
  const canExport = Boolean(metrics.length || SPEC_KEYS.some((k) => present(spec?.[k])));

  const run = async (fmt) => {
    setBusy(fmt);
    try {
      const body = { module, metrics, format: fmt };
      SPEC_KEYS.forEach((k) => { if (present(spec?.[k])) body[k] = spec[k]; });
      if (siteId) body.site_id = siteId;
      if (departmentId) body.department_id = departmentId;
      if (dimensions && Object.keys(dimensions).length) body.dimensions = dimensions;
      if (time) body.time = time;
      if (fyStartMonth) body.fy_start_month = fyStartMonth;
      if (standard) body.standard = standard;
      const { blob, filename } = await exportDashboard(body);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setOpen(false);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        disabled={!canExport}
        className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
      >
        <Download className="h-4 w-4" /> Export
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-40 rounded-md border border-gray-200 bg-white p-1 shadow-lg">
          {FORMATS.map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => run(id)}
              disabled={busy !== null}
              className="flex w-full items-center gap-2 rounded px-2 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {busy === id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />}
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
